<template>
    <b-container class="bv-example-row">
        <alert :message="message" />


        <div v-if="!this.user" class="d-flex justify-content-center mb-3">
            <b-spinner label="Loading..."></b-spinner>
        </div>

        <div v-else>

            <b-row>
                <b-col md="12">
                    <h3>User - {{ this.user.email }}
                    </h3>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <b>Name:</b> {{ this.user.first_name + ' ' + this.user.last_name }}
                </b-col>
                <b-col>
                    <b>Latest entry date:</b> {{ new Date(this.user.latest_entry_date).toLocaleString() }}
                </b-col>
            </b-row>

            <b-row>
                <b-col v-if="this.user.location">
                    <b>Latest login location:</b> {{ this.user.location }}
                </b-col>
                
                <b-col v-if="this.user.company">
                    <b>Company:</b> {{ this.user.company }}
                </b-col>
            </b-row>

            <div class="my-3">
                <h4>Quota extension</h4>
                <b-form class="px-2" @submit="onSubmit">

                    <b-row>
                        <b-col md="9"></b-col>
                        <b-col md="3" class="font-weight-bold text-center">Used amount</b-col>
                    </b-row>
                    <b-row>
                        <b-col md="9">
                            <b-form-group label-cols="4" label-cols-lg="3" label="Max space" label-for="input-max-space">
                                <b-form-input type="number" id="input-max-space" v-model="user.quota_extension.max_space">
                                </b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col md="3" class="col-form-label text-center">{{ this.user.used_space }}</b-col>
                    </b-row>

                    <b-row>
                        <b-col md="9">
                            <b-form-group label-cols="4" label-cols-lg="3" label="Max user" label-for="input-max-user">
                                <b-form-input type="number" id="input-max-user" v-model="user.quota_extension.max_user" />
                            </b-form-group>
                        </b-col>
                        <b-col md="3" class="col-form-label text-center">{{ this.user.used_user }}</b-col>
                    </b-row>

                    <b-row>
                        <b-col md="9">
                            <b-form-group label-cols="4" label-cols-lg="3"
                                :label="'Max storage (' + (this.user.quota_extension.max_storage / 1024 ** 2).toFixed(2) + 'MB)'"
                                label-for="input-max-storage">
                                <b-form-input type="number" id="input-max-storage"
                                    v-model="user.quota_extension.max_storage" />
                            </b-form-group>
                        </b-col>
                        <b-col md="3" class="col-form-label text-center">{{ (this.user.used_storage /
                            1024 ** 2).toFixed(2) + 'MB'
                        }}</b-col>
                    </b-row>

                    <b-row>
                        <b-col md="9">
                            <b-form-group label-cols="4" label-cols-lg="3" label="Can publish"
                                label-for="input-can-publish">
                                <b-form-checkbox class="col-form-label text-center" id="input-can-publish"
                                    v-model="user.quota_extension.can_publish"></b-form-checkbox>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col md="9">
                            <b-form-group label-cols="4" label-cols-lg="3" label="Role" label-for="input-role">
                                <b-form-select placeholder="" id="input-role" v-model="user.role_id"
                                    :options="options" />
                            </b-form-group>
                        </b-col>

                        <b-col md="3" class="text-center">
                            <b-button :disabled="upload_data" type="submit" variant="primary">Update settings <b-spinner
                                    v-if="upload_data" small label="Loading..." /></b-button>
                        </b-col>
                    </b-row>

                </b-form>
            </div>


            <div v-if="this.user.owned_spaces.length > 0">
                <h4>Owned spaces</h4>

                <b-table id="my-table" :fields="owned_space_fields" :items="this.user.owned_spaces" :per-page="perPageSpace"
                    :current-page="currentPageSpace" :sort-by.sync="name" small striped>
                    <template v-slot:cell(name)="data">
                        <router-link :to="'/space/' + data.item.uuid">{{ data.value }}</router-link>
                    </template>
                    <template v-slot:cell(is_public)="data">
                        <label v-if="data.value">&#9989;</label>
                        <label v-else>&#10060;</label>
                    </template>
                    <template v-slot:cell(is_available_web)="data">
                        <label v-if="data.value">&#9989;</label>
                        <label v-else>&#10060;</label>
                    </template>
                </b-table>
            </div>

            <div v-if="this.user.spaces" class="my-3">
                <h4>Space permissions</h4>

                <b-table id="my-table" :fields="fields" :items="this.user.spaces" :per-page="perPageSpace"
                    :current-page="currentPageSpace" :sort-by.sync="name" small striped>
                    <template v-slot:cell(name)="data">
                        <router-link :to="'/space/' + data.item.uuid">{{ data.value }}</router-link>
                    </template>
                    <template v-slot:cell(actions)="data">
                        <b-icon-trash-fill class="text-danger align-middle mr-2" font-scale="1.5"
                            @click="remove_permission(data.item, 'space')" />
                    </template>
                </b-table>
                <b-pagination v-model="currentPageSpace" :total-rows="this.totalRowsSpace" :per-page="perPageSpace"
                    align="fill" size="sm" class="my-0" />
            </div>

            <div class="my-3">
                <h4>Project permissions</h4>

                <b-table id="my-table" :fields="fields" :items="user.projects" :per-page="perPageProject"
                    :current-page="currentPageProject" :sort-by.sync="name" small striped>
                    <template v-slot:cell(name)="data">
                        <router-link :to="'/project/' + data.item.uuid">{{ data.value }}</router-link>
                    </template>
                    <template v-slot:cell(actions)="data">
                        <b-icon-trash-fill class="text-danger align-middle mr-2" font-scale="1.5"
                            @click="remove_permission(data.item, 'project')" />
                    </template>
                </b-table>
                <b-pagination v-model="currentPageProject" :total-rows="this.totalRowsProject" :per-page="perPageProject"
                    align="fill" size="sm" class="my-0" />
            </div>

        </div>

    </b-container>
</template>


<script>
import Alert from '../components/alert.vue';
import { BIconTrashFill } from 'bootstrap-vue';

export default {
    data() {
        return {
            name: 'name',
            user: null,
            message: '',
            perPageSpace: 20,
            currentPageSpace: 1,
            perPageProject: 20,
            currentPageProject: 1,
            totalRowsSpace: null,
            totalRowsProject: null,
            owned_space_fields: [
                { key: 'name', label: 'Name', class: 'text-center align-middle', sortable: true },
                { key: 'uuid', label: 'UUID', class: 'text-center align-middle' },
                { key: 'is_public', label: 'Is public', class: 'text-center align-middle' },
                { key: 'is_available_web', label: 'Available on web?', class: 'text-center align-middle' },
                { key: 'status', label: 'Status', class: 'text-center align-middle' },
            ],
            fields: [
                { key: 'name', label: 'Name', class: 'text-center align-middle', sortable: true },
                { key: 'uuid', label: 'UUID', class: 'text-center align-middle' },
                { key: 'permission_name', label: 'Permission name', class: 'text-center align-middle' },
                { key: 'actions', label: 'Delete permission', class: 'text-center align-middle' }
            ],
            list_of_permission: [
                { text: 'Viewer', value: 1 },
                { text: 'Editor', value: 2 },
                { text: 'Owner', value: 3 }
            ],
            options: [
                { value: null, text: "" },
                { value: 1, text: "Superadmin" },
                { value: 2, text: "Viewer of all spaces" },
                { value: 3, text: "Analyst" }
            ],
            space_uuid: null,
            project_uuid: null,
            space_permission: null,
            list_of_spaces: null,
            permission_fields: { text: "text", value: "value" },
            space_select_fields: { text: "title", value: "where_id" },
            project_select_fields: { text: "name", value: "uuid" },
            list_of_projects: null,
            project_permission: null,
            space_permission: null,
            upload_data: false
        }
    },
    components: {
        alert: Alert,
    },
    methods: {
        init() {
            this.download_user_details()
        },
        download_user_details() {
            this.$http.get(this.$apiURL + '/admin/user/' + this.$route.params.uuid, { headers: this.$store.getters.authHeader }
            )
                .then((res) => {
                    this.user = res.data
                    this.totalRowsSpace = this.user.spaces.length
                    this.totalRowsProject = this.user.projects.length
                })
                .catch((error) => {
                    this.message = {
                        'message': error,
                        'type': "danger"
                    }
                });
        },
        remove_permission(row, type) {
            this.$dialog
                .confirm("Are you sure about the delete of the permission?")
                .then(() => {
                    this.$http.delete(this.$apiURL + '/' + type + '/' + row.uuid + '/share', { headers: this.$store.getters.authHeader, data: [{ email: this.user.email, permission_id: row.permission_id }] }
                    )
                        .then((res) => {
                            this.message = {
                                'message': "You removed " + type + " named '" + row.name + "' succesfully!",
                                'type': "danger"
                            }
                            this.download_user_details()
                        })
                        .catch((error) => {
                            this.message = {
                                'message': error.response.data,
                                'type': "danger"
                            }
                        });
                });
        },
        onSubmit(event) {
            this.upload_data = true;
            event.preventDefault()
            this.$http.patch(this.$apiURL + '/admin/user/' + this.$route.params.uuid, {
                quota_extension: this.user.quota_extension,
                role_id: this.user.role_id
            },
                { headers: this.$store.getters.authHeader }
            )
                .then((res) => {
                    if (res.status == 207) {
                        this.message = { 'message': "User has been updated! User's spaces has been set to suspended, because of the violation of the limits!", type: "warning" }
                    }
                    else {
                        this.message = { 'message': "User has been updated!" };
                    }
                    this.init();
                })
                .catch((error) => {
                    this.message = { 'message': error.response.data, "type": "danger" }
                })
                .finally(() => {
                    this.upload_data = false;
                });
        },
    },
    created() {
        this.init();
    },
    components: {
        BIconTrashFill,
        alert: Alert
    }
}
</script>
